import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleById, articleData } from '@/mocks/articleData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<any>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (id) {
      const found = getArticleById(id);
      setArticle(found);
    }
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-article-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-800 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该文章不存在或已被移除</p>
            <a href="/" className="inline-block px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </a>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  const relatedItems = article.relatedArticles?.map((rid: string) => articleData[rid]).filter(Boolean) || [];

  return (
    <>
      <Navbar />
      <main>
        <Breadcrumb items={[
          { label: '赛事资讯', href: '/category/news' },
          { label: article.category, href: `/category/${article.categorySlug}` },
          { label: article.title },
        ]} />

        <article className="w-full bg-background-50 pb-14 md:pb-20">
          <div className="w-full max-w-[860px] mx-auto px-4 md:px-6">
            <div className="relative h-[300px] md:h-[420px] rounded-2xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6 md:p-8">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 dark:text-foreground-950">
                    {article.category}
                  </span>
                  {article.tags?.map((tag: string) => (
                    <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-background-50/20 backdrop-blur-sm text-background-50">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
                  {article.title}
                </h1>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-6 border-b border-background-200">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {Math.floor(Math.random() * 50000 + 10000).toLocaleString()} 阅读
              </span>
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags?.map((tag: string) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600 border border-background-200">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedItems.length > 0 && (
          <section className="w-full bg-background-100 py-14 md:py-16">
            <div className="w-full max-w-[860px] mx-auto px-4 md:px-6">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedItems.map((rel: any) => (
                  <a
                    key={rel.title}
                    href={`/article/${Object.keys(articleData).find(k => articleData[k].title === rel.title)}`}
                    className="group flex items-start gap-4 bg-background-50 rounded-xl border border-background-200 p-4 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0">
                      <img src={rel.image} alt={rel.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs text-primary-600 font-medium">{rel.category}</span>
                      <h4 className="text-sm font-semibold text-foreground-900 mt-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h4>
                      <span className="text-xs text-foreground-400 mt-1 block">{rel.date}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `{"@context":"https://schema.org","@type":"Article","headline":"${article.title}","datePublished":"${article.date}","author":{"@type":"Person","name":"${article.author}"},"publisher":{"@type":"Organization","name":"kaiyun.com"},"description":"${article.title}"}` }} />
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}